import React, { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

import { PocketbookContent } from './PocketbookContent';

interface Identifiable {
  id: string;
}

interface PocketbookProps<TLeftItem extends Identifiable, TRightItem> {
  trigger: React.ReactNode;
  items: TLeftItem[];
  renderLeftItem: (item: TLeftItem, isSelected: boolean) => React.ReactNode;
  renderRightPanel: (
    selectedItem: TLeftItem | null,
    onSelect: (rightItem: TRightItem) => void,
  ) => React.ReactNode;
  onSelect: (leftItem: TLeftItem, rightItem: TRightItem) => void;
  loading?: boolean;
  searchPlaceholder?: string;
  onSearch?: (items: TLeftItem[], query: string) => TLeftItem[];
  defaultSelectedId?: string;
}

export const Pocketbook = <TLeftItem extends Identifiable, TRightItem>({
  trigger,
  items,
  renderLeftItem,
  renderRightPanel,
  onSelect,
  loading = false,
  searchPlaceholder = 'Search...',
  onSearch,
  defaultSelectedId,
}: PocketbookProps<TLeftItem, TRightItem>) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (leftItem: TLeftItem, rightItem: TRightItem) => {
    onSelect(leftItem, rightItem);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent className="w-[640px] h-[480px] max-h-[80vh] p-4 max-w-[640px] overflow-hidden flex flex-col gap-0">
        <DialogTitle className="sr-only">Carrier Directory</DialogTitle>
        <DialogDescription className="sr-only">
          Search for a carrier and select a division to view contact handles.
        </DialogDescription>
        <PocketbookContent
          items={items}
          renderLeftItem={renderLeftItem}
          renderRightPanel={(selectedItem: TLeftItem | null) =>
            renderRightPanel(selectedItem, (rightItem: TRightItem) =>
              handleSelect(selectedItem as TLeftItem, rightItem),
            )
          }
          loading={loading}
          searchPlaceholder={searchPlaceholder}
          onSearch={onSearch}
          defaultSelectedId={defaultSelectedId}
        />
      </DialogContent>
    </Dialog>
  );
};
